/**
 * Modulo resi Ludoville — frontend.
 * Comunica con il VPS attraverso l'App Proxy: le richieste restano
 * same-origin su www.ludoville.it e Shopify le firma automaticamente.
 */
(() => {
  'use strict';

  const root = document.querySelector('.resi-app');
  if (!root) return;

  const PROXY    = root.dataset.proxy || '/apps/resi';
  const CURRENCY = root.dataset.currency || 'EUR';
  const isStatus = root.classList.contains('resi-status');

  const state = {
    order: null,
    items: [],
    selection: {},        // flid -> { quantity, note, photo_ids[] }
    policy: null,
    type: 'withdrawal',
    estimate: null,
    rma: null,
  };

  // ── utilità ────────────────────────────────────────────────────────
  const $  = (s, c = root) => c.querySelector(s);
  const $$ = (s, c = root) => Array.from(c.querySelectorAll(s));

  const money = (cents, cur = CURRENCY) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: cur })
      .format((cents || 0) / 100);

  const fmtDate = iso =>
    new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });

  // Shopify CDN: aggiunge width= rispettando la query string già presente
  const imgUrl = (url, w) => {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch { return url; }
  };

  const esc = s => String(s ?? '').replace(/[&<>"']/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  class ResiError extends Error {
    constructor(code, fields) { super(code); this.code = code; this.fields = fields || []; }
  }

  const MESSAGES = {
    order_not_found:       "Non troviamo un ordine con questi dati. Controlla il numero e l'email e riprova.",
    order_cancelled:       'Questo ordine risulta annullato: contattaci e lo verifichiamo insieme.',
    no_orders:             'Non risultano ordini associati a questo account.',
    not_shipped:           'Questo ordine non è ancora stato spedito.',
    not_delivered:         'La consegna non risulta ancora completata: la finestra di reso partirà da quel momento.',
    window_expired:        'Il periodo per richiedere il reso di questo ordine è terminato.',
    line_not_returnable:   'Uno degli articoli selezionati non risulta più rendibile. Ricarica la pagina.',
    invalid_quantity:      'La quantità richiesta non è disponibile per il reso.',
    partial_not_allowed:   'Per questo ordine il reso va richiesto per l\'intera quantità.',
    photo_required:        'Per questo motivo serve almeno una foto dell\'articolo.',
    policy_not_accepted:   'Devi accettare le condizioni di reso per proseguire.',
    invalid_input:         'Controlla i campi evidenziati.',
    invalid_type:          'Seleziona un motivo valido.',
    invalid_code:          'Il numero di pratica non è nel formato corretto (es. LV-2026-000123).',
    rma_not_found:         'Non troviamo questa pratica. Controlla il codice e l\'email.',
    tracking_not_allowed:  'Il tracking può essere inserito solo dopo l\'approvazione del reso.',
    invalid_tracking:      'Il numero di tracking non sembra valido.',
    no_lines:              'Seleziona almeno un articolo da restituire.',
    no_file:               'Nessun file selezionato.',
    file_too_large:        'Il file supera 8 MB. Prova a ridurne le dimensioni.',
    file_type_not_allowed: 'Formato non supportato. Usa JPG, PNG, WEBP o PDF.',
    invalid_image:         'Non riusciamo a leggere questa immagine.',
    upload_error:          'Caricamento non riuscito. Riprova.',
    rate_limited:          'Troppi tentativi ravvicinati. Riprova tra qualche minuto.',
    route_not_found:       'Funzione non disponibile.',
    internal_error:        'Si è verificato un errore. Riprova o scrivici a store@ludoville.it.',
    network:               'Connessione non riuscita. Controlla la rete e riprova.',
  };
  const msg = c => MESSAGES[c] || MESSAGES.internal_error;

  async function api(path, { method = 'GET', body, form, query } = {}) {
    const url  = new URL(`${PROXY}/${path}`, window.location.origin);
    if (query) Object.entries(query).forEach(([k, v]) => v != null && url.searchParams.set(k, v));

    const opts = { method, headers: {} };
    if (form)      opts.body = form;
    else if (body) { opts.headers['Content-Type'] = 'application/json';
                     opts.body = JSON.stringify(body); }

    let res;
    try {
      res = await fetch(url.toString(), opts);
    } catch {
      throw new ResiError('network');
    }

    let json;
    try { json = await res.json(); }
    catch { throw new ResiError('internal_error'); }

    if (!json.ok) throw new ResiError(json.error, json.fields);
    return json.data;
  }

  // ── navigazione ────────────────────────────────────────────────────
  function showStep(n) {
    $$('.resi-step').forEach(s => { s.hidden = Number(s.dataset.step) !== n; });
    $$('.resi-progress li').forEach(li => {
      const p = Number(li.dataset.progress);
      li.classList.toggle('is-current', p === n);
      li.classList.toggle('is-done', p < n);
    });
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const first = $(`.resi-step[data-step="${n}"] input, .resi-step[data-step="${n}"] button`);
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 250);
  }

  function showError(step, code) {
    const el = $(`.resi-step[data-step="${step}"] .resi-error`)
            || $('.resi-error');
    if (!el) return;
    el.textContent = msg(code);
    el.hidden = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function clearError(step) {
    const el = $(`.resi-step[data-step="${step}"] .resi-error`);
    if (el) el.hidden = true;
  }

  function busy(btn, label) {
    btn.disabled = true;
    btn.dataset.prev = btn.textContent;
    btn.textContent = label;
  }
  function unbusy(btn) {
    btn.disabled = false;
    if (btn.dataset.prev) btn.textContent = btn.dataset.prev;
  }

  // ═══════════════════════════════════════════════════════════════════
  //  FLUSSO RICHIESTA RESO
  // ═══════════════════════════════════════════════════════════════════
  if (!isStatus) {

    // ── STEP 1 ─────────────────────────────────────────────────────
    $('#resi-lookup')?.addEventListener('submit', async ev => {
      ev.preventDefault();
      clearError(1);
      const btn = ev.target.querySelector('button[type=submit]');
      busy(btn, 'Verifico…');

      try {
        const data = await api('lookup.php', {
          method: 'POST',
          body: {
            order_number: $('#order_number')?.value.trim(),
            email:        $('#email')?.value.trim(),
            type:         state.type,
          },
        });
        state.order  = data.order;
        state.items  = data.items;
        state.policy = data.policy;
        state.selection = {};

        if (!data.items.length) {
          showError(1, 'line_not_returnable');
          return;
        }
        renderStep2(data);
        showStep(2);
      } catch (e) {
        showError(1, e.code);
      } finally {
        unbusy(btn);
      }
    });

    // ── STEP 2 ─────────────────────────────────────────────────────
    function renderStep2(data) {
      $('.resi-order-summary').innerHTML =
        `<p>Ordine <strong>${esc(data.order.name)}</strong> del ${fmtDate(data.order.date)}</p>`;

      const dl = $('.resi-deadline');
      if (data.eligibility?.deadline) {
        dl.textContent = data.eligibility.eligible
          ? `Hai tempo fino al ${fmtDate(data.eligibility.deadline)} per inviare questa richiesta.`
          : `Il termine per il reso era il ${fmtDate(data.eligibility.deadline)}.`;
        dl.classList.toggle('resi-deadline--expired', !data.eligibility.eligible);
        dl.hidden = false;
      } else {
        dl.hidden = true;
      }

      $('.resi-items').innerHTML = data.items.map((it, i) => `
        <div class="resi-item" data-flid="${esc(it.fulfillment_line_item_id)}">
          <div class="resi-item__pick">
            <input type="checkbox" id="resi-it-${i}" data-pick>
          </div>
          ${it.image
            ? `<img class="resi-item__img" src="${esc(imgUrl(it.image, 140))}" alt="" width="70" height="70" loading="lazy">`
            : '<div class="resi-item__img resi-item__img--ph" aria-hidden="true"></div>'}
          <div class="resi-item__body">
            <label for="resi-it-${i}" class="resi-item__title">${esc(it.title)}</label>
            ${it.variant_title ? `<div class="resi-item__variant">${esc(it.variant_title)}</div>` : ''}
            <div class="resi-item__price">${money(it.unit_price_cents, it.currency)}</div>
            <div class="resi-item__controls" hidden>
              <label class="resi-item__qty">
                Quantità
                <select data-qty>
                  ${Array.from({ length: it.max_quantity },
                      (_, n) => `<option value="${n + 1}">${n + 1}</option>`).join('')}
                </select>
              </label>
              <label class="resi-item__note">
                Dettagli (facoltativo)
                <input type="text" data-note maxlength="300">
              </label>
              <div class="resi-item__photos" hidden>
                <label class="resi-item__file">
                  Foto dell'articolo
                  <input type="file" data-photo accept="image/*,application/pdf" multiple>
                </label>
                <ul class="resi-item__filelist"></ul>
              </div>
            </div>
          </div>
        </div>`).join('');

      bindItems();
      syncPhotoRequirement();
    }

    function bindItems() {
      $$('.resi-item').forEach(row => {
        const flid    = row.dataset.flid;
        const pick    = $('[data-pick]', row);
        const ctrls   = $('.resi-item__controls', row);
        const qty     = $('[data-qty]', row);
        const note    = $('[data-note]', row);
        const photo   = $('[data-photo]', row);
        const list    = $('.resi-item__filelist', row);

        pick.addEventListener('change', () => {
          ctrls.hidden = !pick.checked;
          row.classList.toggle('is-selected', pick.checked);
          if (pick.checked) {
            state.selection[flid] = state.selection[flid]
              || { quantity: Number(qty.value), note: '', photo_ids: [] };
          } else {
            delete state.selection[flid];
          }
        });

        qty.addEventListener('change', () => {
          if (state.selection[flid]) state.selection[flid].quantity = Number(qty.value);
        });

        note.addEventListener('input', () => {
          if (state.selection[flid]) state.selection[flid].note = note.value;
        });

        photo.addEventListener('change', async () => {
          for (const file of photo.files) await uploadPhoto(file, flid, list);
          photo.value = '';
        });
      });
    }

    async function uploadPhoto(file, flid, listEl) {
      const li = document.createElement('li');
      li.textContent = `${file.name} — caricamento…`;
      listEl.appendChild(li);

      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api('return/upload', { method: 'POST', form: fd });

        state.selection[flid] = state.selection[flid] || { quantity: 1, note: '', photo_ids: [] };
        state.selection[flid].photo_ids.push(res.file_id);

        li.textContent = `${file.name} ✓`;
        li.className = 'is-ok';
      } catch (e) {
        li.textContent = `${file.name} — ${msg(e.code)}`;
        li.className = 'is-error';
      }
    }

    function syncPhotoRequirement() {
      const need = (state.policy?.settings?.require_photo_for || []).includes(state.type);
      $$('.resi-item__photos').forEach(el => { el.hidden = !need; });
    }

    $$('input[name="type"]').forEach(r => {
      r.addEventListener('change', () => {
        state.type = r.value;
        syncPhotoRequirement();
      });
    });

    $$('[data-back]').forEach(b => b.addEventListener('click', () => {
      const cur = Number(b.closest('.resi-step').dataset.step);
      showStep(cur - 1);
    }));

    $('[data-next]')?.addEventListener('click', () => {
      clearError(2);
      const picked = Object.keys(state.selection);
      if (!picked.length) { showError(2, 'no_lines'); return; }

      const need = (state.policy?.settings?.require_photo_for || []).includes(state.type);
      if (need) {
        const missing = picked.find(f => !(state.selection[f].photo_ids || []).length);
        if (missing) { showError(2, 'photo_required'); return; }
      }
      renderStep3();
      showStep(3);
    });

    // ── STEP 3 ─────────────────────────────────────────────────────
    function renderStep3() {
      const byId = Object.fromEntries(state.items.map(i => [i.fulfillment_line_item_id, i]));

      $('.resi-recap').innerHTML = `
        <p class="resi-help">Ordine ${esc(state.order.name)}</p>
        <ul class="resi-recap__list">
          ${Object.entries(state.selection).map(([flid, sel]) => {
            const it = byId[flid];
            return `<li><strong>${esc(it.title)}</strong>
              ${it.variant_title ? ` — ${esc(it.variant_title)}` : ''}
              <span class="resi-recap__qty">× ${sel.quantity}</span></li>`;
          }).join('')}
        </ul>`;

      // stima lato client (il server ricalcola comunque tutto)
      let goods = 0, isFull = true;
      state.items.forEach(it => {
        const sel = state.selection[it.fulfillment_line_item_id];
        const q = sel ? sel.quantity : 0;
        goods += it.unit_price_cents * q;
        if (q < it.max_quantity) isFull = false;
      });

      const s    = state.policy?.settings || {};
      const ship = isFull && s.refund_original_shipping !== 'none'
        ? Math.round(parseFloat(state.order.shipping || '0') * 100) : 0;
      const fee  = Number(s.return_shipping_fee_cents || 0);
      const tot  = Math.max(0, goods + ship - fee);

      state.estimate = { goods, ship, fee, tot };

      $('[data-cost="goods"]').textContent      = money(goods);
      $('[data-cost="shipping"]').textContent   = money(ship);
      $('[data-cost="return_fee"]').textContent = fee ? '− ' + money(fee) : money(0);
      $('[data-cost="total"]').textContent      = money(tot);

      $('[data-cost-row="shipping"]').hidden   = ship === 0;
      $('[data-cost-row="return_fee"]').hidden = fee === 0;

      $('[data-cost-note]').textContent =
        s.return_shipping_paid_by === 'customer'
          ? 'Le spese di spedizione per la restituzione sono a tuo carico.'
          : 'Le spese di spedizione per la restituzione sono a nostro carico.';
    }

    $('[data-submit]')?.addEventListener('click', async ev => {
      clearError(3);
      const btn = ev.currentTarget;

      if (!$('#policy_accepted').checked) { showError(3, 'policy_not_accepted'); return; }
      if (btn.disabled) return;                       // niente doppio invio

      busy(btn, 'Invio in corso…');

      try {
        const data = await api('create.php', {
          method: 'POST',
          body: {
            order_gid: state.order.gid,
            type:      state.type,
            note:      $('#note')?.value || '',
            email:     $('#email')?.value.trim() || root.dataset.customerEmail,
            policy_accepted: true,
            lines: Object.entries(state.selection).map(([flid, sel]) => ({
              fulfillment_line_item_id: flid,
              quantity:  sel.quantity,
              note:      sel.note,
              photo_ids: sel.photo_ids || [],
            })),
          },
        });

        const statusPage = root.dataset.statusUrl || '/pages/stato-reso';
        $('.resi-success').innerHTML = `
          <div class="resi-success__icon" aria-hidden="true">✓</div>
          <h2>Richiesta registrata</h2>
          <p>Il numero della tua pratica è <strong>${esc(data.rma_code)}</strong>.</p>
          <p>Ti abbiamo inviato un'email di conferma con il riepilogo.
             Appena verificata la richiesta ti scriviamo con l'esito e le
             istruzioni per la spedizione.</p>
          <p class="resi-actions">
            <a class="resi-btn resi-btn--primary"
               href="${statusPage}?code=${encodeURIComponent(data.rma_code)}">
               Segui la tua pratica</a>
          </p>`;
        showStep(4);
      } catch (e) {
        showError(3, e.code);
      } finally {
        unbusy(btn);
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  //  PAGINA STATO PRATICA
  // ═══════════════════════════════════════════════════════════════════
  if (isStatus) {

    async function loadStatus(code, email) {
      const data = await api('status.php', { query: { code, email } });
      state.rma = data;

      const t = data.timeline || [];
      $('.resi-status-detail').innerHTML = `
        <div class="resi-status__head">
          <h2>${esc(data.rma_code)}</h2>
          <span class="resi-badge">${esc(data.status_label)}</span>
        </div>
        <p class="resi-help">Ordine ${esc(data.order_name)}</p>

        ${data.decline_note ? `
          <div class="resi-callout resi-callout--warn">
            <strong>Esito:</strong> ${esc(data.decline_note)}
          </div>` : ''}

        <ol class="resi-timeline">
          ${t.map(s => `<li><span class="resi-timeline__dot"></span>
            <div><strong>${esc(s.label)}</strong>
            <small>${new Date(s.at).toLocaleString('it-IT')}</small></div></li>`).join('')}
        </ol>

        <h3>Articoli</h3>
        <ul class="resi-recap__list">
          ${(data.items || []).map(i => `<li>${esc(i.title)}
            ${i.variant_title ? ' — ' + esc(i.variant_title) : ''}
            <span class="resi-recap__qty">× ${i.quantity}</span></li>`).join('')}
        </ul>

        ${data.tracking ? `
          <h3>Spedizione di ritorno</h3>
          <p>${esc(data.tracking.carrier)} — ${esc(data.tracking.number)}
             ${data.tracking.url
               ? `<br><a href="${esc(data.tracking.url)}" target="_blank" rel="noopener">Traccia il pacco</a>`
               : ''}</p>` : ''}

        ${data.can_add_tracking && data.return_address ? `
          <h3>Dove spedire</h3>
          <address class="resi-address">
            ${esc(data.return_address.name)}<br>
            ${esc(data.return_address.line1)}<br>
            ${esc(data.return_address.zip)} ${esc(data.return_address.city)}<br>
            ${esc(data.return_address.country)}
          </address>` : ''}

        ${data.refunded != null
          ? `<div class="resi-callout resi-callout--ok">
               Rimborso emesso: <strong>${money(data.refunded, data.currency)}</strong>
             </div>` : ''}`;

      $('.resi-tracking-form').hidden = !data.can_add_tracking;
      showStep(2);
    }

    $('#resi-status-lookup')?.addEventListener('submit', async ev => {
      ev.preventDefault();
      clearError(1);
      const btn = ev.target.querySelector('button[type=submit]');
      busy(btn, 'Cerco…');
      try {
        await loadStatus($('#rma_code').value.trim().toUpperCase(),
                         $('#status_email')?.value.trim());
      } catch (e) {
        showError(1, e.code);
      } finally {
        unbusy(btn);
      }
    });

    $('[data-save-tracking]')?.addEventListener('click', async ev => {
      clearError(2);
      const btn = ev.currentTarget;
      const carrier  = $('#carrier').value.trim();
      const tracking = $('#tracking').value.trim();
      if (!carrier || !tracking) { showError(2, 'invalid_input'); return; }

      busy(btn, 'Salvo…');
      try {
        await api('return/tracking', {
          method: 'POST',
          body: {
            code:     state.rma.rma_code,
            email:    $('#status_email')?.value.trim(),
            carrier, tracking,
          },
        });
        await loadStatus(state.rma.rma_code, $('#status_email')?.value.trim());
      } catch (e) {
        showError(2, e.code);
      } finally {
        unbusy(btn);
      }
    });

    // apertura diretta da link email: ?code=LV-...
    const params = new URLSearchParams(location.search);
    const code   = params.get('code');
    if (code) {
      $('#rma_code').value = code.toUpperCase();
      if (root.dataset.loggedIn === 'true') {
        loadStatus(code.toUpperCase(), null).catch(e => showError(1, e.code));
      }
    }
  }
})();
